import { maybe, rd, type RemoteData } from '@passionware/monads';
import { createSimpleEvent } from '@passionware/simple-event';
import type { Pin } from '__types__';
import { Plus, Tag, Trash2 } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MediaItemSidebar from 'v1/components/modals/MediaModals/MediaFocusModal/MediaItemSidebar/MediaItemSidebar';
import {
  PIN_TAGS_UNIT_ID,
  type PinTag
} from 'v1/components/modals/MediaModals/MediaFocusModal/MediaItemSidebar/PinTagsUnit';
import type { EnumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import { Button } from 'v5/design-system/Button';
import { DropdownMenuItem } from 'v5/design-system/DropdownMenu';
import { BatchActionsLayout } from 'v5/patterns/entity-list/BatchActionsLayout';
import { EntityEmptyState } from 'v5/patterns/entity-list/EntityEmptyState';
import { EntityListHeader } from 'v5/patterns/entity-list/EntityListHeader';
import { TileSizeMenu } from 'v5/patterns/entity-list/TileSizeMenu';
import {
  TILE_MIN_WIDTH_PX,
  type TileSize
} from 'v5/patterns/entity-list/tileSize';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';
import { EnumFilterWidget } from 'v5/patterns/filters/EnumFilterWidget';
import {
  MediaGallery,
  type MediaGalleryItem,
  MediaGallerySkeleton
} from 'v5/patterns/forms/fields/image-upload/MediaGallery';
import { useLocalPreviews } from 'v5/patterns/forms/fields/image-upload/useLocalPreviews';
import {
  type PendingUpload,
  useMediaUploads
} from 'v5/patterns/forms/fields/image-upload/useMediaUploads';
import type { UnitTarget } from 'v5/patterns/forms/schema-form/schemaForm.types';
import { PageInsetListLayout } from 'v5/patterns/page-layout/PageInsetListLayout';
import { resolvePinMedia } from 'v5/patterns/widgets/MediaCarousel/mediaCarousel.resolver';
import { PinViewer } from 'v5/patterns/widgets/PinViewer/PinViewer';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithToastService } from 'v5/services/front/ToastService/ToastService';
import {
  DEFAULT_PINBOARD_TILE_SIZE,
  type WithUserPreferencesService
} from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import type { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import type { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import type { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import type { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { useTagCreationAllowed } from 'v5/services/internal/SystemSettingsService/useTagCreationAllowed';
import { createUseOptimisticState } from 'v5/platform/react/useOptimisticState';
import { AddPinsDialog, AddPinsZone } from './AddPinsDialog';
import { usePasteIntoBoard } from './usePasteIntoBoard';
import { type PinLinkWrite, pinboardUtils } from './pinboard.logic';
import { PinMenu, PinTags } from './PinTile';

type PinsAction = { type: 'reorder'; orderedIds: number[] };

const usePinsOptimistic = createUseOptimisticState<Pin[], PinsAction>({
  reducer: (pins, action) => pinboardUtils.withOrder(pins, action.orderedIds)
});

/** Any file, as v1 took; a larger one is refused with a toast. */
const ACCEPT = '*/*';
const MAX_SIZE = 100 * 1024 * 1024;
/** How long a deleted pin can be got back. */
export const UNDO_MS = 6000;

export interface PinboardProps
  extends WithServices<
    [
      WithUploadService,
      WithToastService,
      WithUserPreferencesService,
      WithAppScopeService,
      WithSystemSettingsService,
      WithAccessService
    ]
  > {
  pins: RemoteData<Pin[]>;
  /** Shows the pins without adding, editing, reordering or deleting. */
  readOnly: boolean;
  /** The pin in view as the board opens (a picture pressed elsewhere). */
  initialPinId?: number;
  onCreatePin(write: PinLinkWrite & { file_id?: string }): Promise<unknown>;
  onDeletePin(pinId: number): Promise<unknown>;
  /** The pin with its whole new list of tags. */
  onUpdatePinTags(pin: Pin, tags: PinTag[]): Promise<unknown>;
  /** Every live pin's id in the new order. */
  onReorderPins(orderedIds: number[]): Promise<unknown>;
  /** The picture's address becomes the profile's picture. */
  onSetProfilePicture(url: string): Promise<unknown>;
}

/**
 * A resource's pins in the gallery grid the Documents tab uses, each picture whole on its blurred
 * copy. A tag filter narrows it; a press opens the pin in the viewer with its details beside.
 * Unless read only: files are dropped, pasted or picked on the gallery, links come from Add pins;
 * tags are edited in the viewer; tiles move by drag or from the keyboard (while no filter is on);
 * picked tiles are deleted from the batch menu or with Delete.
 */
export function Pinboard(props: PinboardProps) {
  const { t } = useTranslation('resources', { keyPrefix: 'Pinboard' });
  const reorders = useRef(0);
  // a new order shows at once; a failed save puts the saved one back
  const reorder = usePinsOptimistic({
    id: maybe.ofAbsent(),
    entity: props.pins,
    executeAction: async action => {
      try {
        await props.onReorderPins(action.orderedIds);
      } catch (error) {
        props.services.toastService.open({
          type: 'error',
          message: t('toast.reorderFailed')
        });
        throw error;
      }
    }
  });
  // from the Size menu: mine, the same on every resource
  const tileSizeStore = props.services.userPreferencesService.pinboardTileSize;
  const tileSize = rd.mapOrElse(
    tileSizeStore.useValue(),
    value => value,
    DEFAULT_PINBOARD_TILE_SIZE
  );
  return rd
    .journey(reorder.entity)
    .wait(
      <PinboardFrame
        count={null}
        content={
          <MediaGallerySkeleton
            label={t('loading')}
            tileMinWidth={`${TILE_MIN_WIDTH_PX[tileSize]}px`}
          />
        }
      />
    )
    .catch(error => <PinboardFrame count={null} content={renderError(error)} />)
    .map(pins => (
      <PinboardContent
        {...props}
        pins={pins}
        onReorderPins={orderedIds =>
          reorder.scheduleAction(`reorder-${++reorders.current}`, {
            type: 'reorder',
            orderedIds
          })
        }
        tileSize={tileSize}
        onTileSizeChange={value => void tileSizeStore.setValue(value)}
      />
    ));
}

/** The tab's chrome, as the Documents tab has it: the title with the count, the actions on its row. */
function PinboardFrame({
  count,
  actions = null,
  content
}: {
  count: number | null;
  actions?: ReactNode;
  content: ReactNode;
}) {
  const { t } = useTranslation('resources', { keyPrefix: 'Pinboard' });
  return (
    <div className="tailwind-root contents">
      <PageInsetListLayout
        className="bg-transparent"
        header={
          <EntityListHeader
            title={t('title')}
            count={count === null ? rd.ofPending() : rd.of(count)}
          />
        }
        actions={actions}
        // This scroll area clips at its top edge, where the tiles start: room for their focus ring.
        contentClassName="pt-[4px]"
        content={content}
      />
    </div>
  );
}

function PinboardContent({
  services,
  pins,
  readOnly,
  initialPinId,
  onCreatePin,
  onDeletePin,
  onUpdatePinTags,
  onReorderPins,
  onSetProfilePicture,
  tileSize,
  onTileSizeChange
}: Omit<PinboardProps, 'pins'> & {
  pins: Pin[];
  tileSize: TileSize;
  onTileSizeChange(value: TileSize): void;
}) {
  const { t } = useTranslation('resources', { keyPrefix: 'Pinboard' });
  const { toastService } = services;
  const allowCreateTags = useTagCreationAllowed(services);
  const [tagFilter, setTagFilter] = useState<EnumFilter<string> | null>(null);
  const [viewerPinId, setViewerPinId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  /** The address pasted over the board, which the link field starts with. */
  const [pastedLink, setPastedLink] = useState<string>();
  /** Pins deleted a moment ago: gone from the board until the delete is sent, back on Undo. */
  const [deleting, setDeleting] = useState<ReadonlySet<number>>(new Set());
  /** Picked tiles, by key; only the ones shown count. */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const uploads = useMediaUploads(services.uploadService);
  /** Where each file came in the drop, so its tile keeps that place while it uploads and attaches. */
  const dropOrder = useRef(new WeakMap<File, number>());
  const drops = useRef(0);
  /** The local copy of each uploaded picture, shown by its pin in place of the remote one. */
  const previews = useLocalPreviews();
  /** Uploaded files being pinned, still shown as uploads. */
  const [attaching, setAttaching] = useState<PendingUpload[]>([]);
  // copies of pins that are gone (a refetch, another tab) are released; a pending attach keeps its own
  useEffect(() => {
    const keep = new Set([
      ...pins.flatMap(pin => pinboardUtils.fileIdOf(pin) ?? []),
      ...attaching.map(upload => upload.id.replace('attach-', ''))
    ]);
    previews.releaseExcept(keep);
  }, [pins, attaching, previews]);
  /** Edit tags from a tile's menu: the viewer, as it opens, puts its Tags unit in edit mode. */
  const editTagsOnOpen = useRef(false);
  const editTagsSignal = useMemo(() => createSimpleEvent<UnitTarget>(), []);

  const orderedNow = useRef<Pin[]>([]);
  const ordered = pinboardUtils.ordered(
    pins.filter(pin => !deleting.has(pin.id))
  );
  orderedNow.current = ordered;
  const shown = ordered.filter(pin => pinboardUtils.matches(pin, tagFilter));
  const filtered = pinboardUtils.isFilterActive(tagFilter);
  const tagOptions = pinboardUtils.tagOptions(ordered);
  // in the order dropped, whichever upload finishes first
  const pending = [...uploads.uploads, ...attaching].sort(
    (a, b) =>
      (dropOrder.current.get(a.file) ?? 0) -
      (dropOrder.current.get(b.file) ?? 0)
  );
  const selected = shown.filter(pin => picked.has(String(pin.id)));
  const pinOf = (key: string) => shown.find(pin => String(pin.id) === key);

  const boardRef = useRef<HTMLDivElement>(null);
  // Once, as the board opens: scrolling it afterwards is the user's.
  useEffect(() => {
    const gallery = boardRef.current;
    if (initialPinId === undefined || !gallery) return;
    const scrolled = () => {
      const tile = gallery.querySelector(`[data-media-key="${initialPinId}"]`);
      tile?.scrollIntoView({ block: 'center' });
      return tile !== null;
    };
    if (scrolled()) return;
    // the gallery's tiles come in a render after its own
    const observer = new MutationObserver(() => {
      if (scrolled()) observer.disconnect();
    });
    observer.observe(gallery, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [initialPinId]);
  // The last pin shown left the filter (it lost a tag in the viewer): the viewer closes for good,
  // in this render (React's state adjusted while rendering, no effect and no frame with it open).
  if (viewerPinId !== null && shown.length === 0) setViewerPinId(null);

  /**
   * The files dropped, pasted or picked: all uploaded at once, pinned one after another in the
   * order they came, one toast for the batch. `index`: where among the live pins they land (the
   * place the pointer shows on a paste); the end without it, and whenever a tag filter narrows the
   * board.
   */
  const addFiles = async (files: File[], index: number | null = null) => {
    // the pin the files land after: null at the start; undefined without an index
    const anchor =
      index === null
        ? undefined
        : index === 0
          ? null
          : (ordered[Math.min(index, ordered.length) - 1]?.id ?? null);
    files.forEach(file => dropOrder.current.set(file, ++drops.current));
    const uploaded = files.map(async file => {
      const outcome = await uploads.upload(file);
      if (outcome.type === 'failed') return null;
      const { fileId, accessUrl, previewUrl = null } = outcome.media;
      const id = `attach-${fileId}`;
      setAttaching(current => [
        ...current,
        { id, file, previewUrl, status: 'uploading', progress: 100 }
      ]);
      if (previewUrl) previews.keep(fileId, previewUrl);
      return { id, fileId, accessUrl };
    });
    let added = 0;
    let failed = 0;
    const createdIds: (number | null)[] = [];
    for (const pending of uploaded) {
      const attached = await pending;
      if (attached === null) {
        failed += 1;
        continue;
      }
      try {
        const created = await onCreatePin({
          file_id: attached.fileId,
          url: attached.accessUrl,
          pin_type: 'file'
        });
        createdIds.push(pinboardUtils.idOf(created));
        added += 1;
      } catch {
        previews.release(attached.fileId);
        failed += 1;
      } finally {
        setAttaching(current =>
          current.filter(upload => upload.id !== attached.id)
        );
      }
    }
    const landed = createdIds.filter((id): id is number => id !== null);
    if (anchor !== undefined && !filtered && landed.length > 0) {
      // after the pin the pointer was on, judged on the board as it is now (pins added meanwhile
      // keep their place); one save of the whole order
      const fresh = new Set(landed);
      const live = orderedNow.current
        .map(pin => pin.id)
        .filter(id => !fresh.has(id));
      const after = anchor === null ? -1 : live.indexOf(anchor);
      const at = anchor !== null && after === -1 ? live.length : after + 1;
      void onReorderPins([...live.slice(0, at), ...landed, ...live.slice(at)]);
    }
    const summary = pinboardUtils.addSummaryOf(added, failed);
    switch (summary.type) {
      case 'added':
        toastService.open({
          type: 'success',
          message: t('toast.addedMany', { count: summary.count })
        });
        return;
      case 'failed':
        toastService.open({
          type: 'error',
          message: t('toast.addFailedMany', { count: summary.count })
        });
        return;
      case 'partly':
        toastService.open({
          type: 'error',
          message: t('toast.addedPartly', {
            added: summary.added,
            failed: summary.failed,
            count: summary.failed
          })
        });
        return;
    }
  };
  const addLink = async (write: PinLinkWrite) => {
    setPastedLink(undefined);
    try {
      await onCreatePin(write);
      toastService.open({ type: 'success', message: t('toast.added') });
    } catch {
      toastService.open({ type: 'error', message: t('toast.addFailed') });
    }
  };
  /**
   * One pin from its menu or Delete, or the selection from the batch menu: they leave the board
   * at once and are deleted once the toast's Undo has gone; one toast a call.
   */
  const remove = (doomed: Pin[]) => {
    const ids = doomed.map(pin => pin.id);
    const settle = () =>
      setDeleting(current => {
        const next = new Set(current);
        ids.forEach(id => next.delete(id));
        return next;
      });
    setDeleting(current => new Set([...current, ...ids]));
    setPicked(current => {
      const next = new Set(current);
      ids.forEach(id => next.delete(String(id)));
      return next;
    });
    const timer = setTimeout(async () => {
      const results = await Promise.allSettled(
        doomed.map(async pin => {
          await onDeletePin(pin.id);
          const fileId = pinboardUtils.fileIdOf(pin);
          if (fileId !== null) previews.release(fileId);
        })
      );
      settle();
      const failed = results.filter(result => result.status === 'rejected');
      if (failed.length > 0) {
        toastService.open({
          type: 'error',
          message: t('toast.deleteFailed', { count: failed.length })
        });
      }
    }, UNDO_MS);
    toastService.open({
      type: 'success',
      message: t('toast.deleted', { count: ids.length }),
      autoClose: UNDO_MS,
      action: {
        label: t('toast.undo'),
        onClick: () => {
          clearTimeout(timer);
          settle();
        }
      }
    });
  };
  const saveTags = async (pin: Pin, tags: PinTag[]) => {
    await onUpdatePinTags(pin, tags);
    toastService.open({ type: 'success', message: t('toast.updated') });
  };
  const setAsProfilePicture = async (pin: Pin) => {
    const url = pinboardUtils.hrefOf(pin);
    if (url === null) return;
    try {
      await onSetProfilePicture(url);
      toastService.open({
        type: 'success',
        message: t('toast.profilePictureSaved')
      });
    } catch {
      toastService.open({
        type: 'error',
        message: t('toast.profilePictureFailed')
      });
    }
  };

  const boardIsEmpty =
    !readOnly && ordered.length === 0 && pending.length === 0;
  usePasteIntoBoard({
    enabled: !readOnly && !adding && viewerPinId === null,
    board: boardRef,
    // on an empty board the link field is on the page; else Add pins opens with it
    onLink: address => {
      setPastedLink(address);
      if (!boardIsEmpty) setAdding(true);
    },
    // with pins the gallery takes the files, where the pointer is
    onFiles: boardIsEmpty ? files => void addFiles(files) : undefined
  });

  const localPreviewOf = (pin: Pin) => {
    const fileId = pinboardUtils.fileIdOf(pin);
    return fileId === null ? undefined : previews.previewOf(fileId);
  };
  const media = new Map(
    shown.map(pin => {
      const preview = localPreviewOf(pin);
      return [
        pin.id,
        preview === undefined
          ? resolvePinMedia(pin)
          : { mediaType: 'image' as const, src: preview }
      ];
    })
  );
  const items = shown.map((pin): MediaGalleryItem => {
    const picture = media.get(pin.id) ?? null;
    return {
      key: String(pin.id),
      name: pinboardUtils.nameOf(pin) ?? t('card.untitled'),
      // what the tile shows: the picture, the video's poster, or the kind of file or link
      mimeType: pinboardUtils.mimeTypeOf(pin, picture?.mediaType ?? null),
      src: picture?.src ?? pinboardUtils.hrefOf(pin) ?? '',
      poster: picture?.mediaType === 'video' ? picture.src : undefined,
      fallbackSrc: picture?.fallbackSrc,
      localCopy: localPreviewOf(pin) !== undefined,
      caption: <PinTags pin={pin} />,
      actions: (
        <PinMenu
          pin={pin}
          onEditTags={
            readOnly
              ? undefined
              : () => {
                  editTagsOnOpen.current = true;
                  setViewerPinId(pin.id);
                }
          }
          onSetAsProfilePicture={
            !readOnly && pinboardUtils.canBeProfilePicture(pin)
              ? () => void setAsProfilePicture(pin)
              : undefined
          }
          onDelete={readOnly ? undefined : () => remove([pin])}
        />
      )
    };
  });

  const content = (() => {
    // nothing yet: where pins are added from, in place of the board
    if (boardIsEmpty) {
      return (
        <AddPinsZone
          key={pastedLink}
          initialLink={pastedLink}
          onFiles={addFiles}
          onAddLink={write => void addLink(write)}
        />
      );
    }
    if (shown.length === 0 && pending.length === 0 && ordered.length > 0) {
      return (
        <EntityEmptyState
          title={t('noMatchesTitle')}
          message={t('noMatches')}
          clearable
          onClear={() => setTagFilter(null)}
        />
      );
    }
    return (
      <MediaGallery
        services={services}
        items={items}
        uploads={pending}
        onOpen={key => setViewerPinId(Number(key))}
        onAddFiles={
          readOnly ? undefined : (files, index) => addFiles(files, index)
        }
        onRemove={
          readOnly
            ? undefined
            : key => {
                const pin = pinOf(key);
                if (pin) remove([pin]);
              }
        }
        onReorder={
          readOnly || filtered
            ? undefined
            : keys => void onReorderPins(keys.map(Number))
        }
        selectedKeys={readOnly ? undefined : picked}
        onSelectedKeysChange={setPicked}
        onRetryUpload={upload => {
          uploads.dismiss(upload.id);
          void addFiles([upload.file]);
        }}
        onDismissUpload={uploads.dismiss}
        accept={ACCEPT}
        maxSize={MAX_SIZE}
        multiple
        addTile={<MediaGallery.AddTile />}
        emptyText={t('emptyReadOnly')}
        tileMinWidth={`${TILE_MIN_WIDTH_PX[tileSize]}px`}
      />
    );
  })();

  return (
    <>
      <PinboardFrame
        count={ordered.length}
        actions={
          <>
            {/* as v1: there whenever there are pins, tagged or not */}
            {ordered.length > 0 && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <TileSizeMenu value={tileSize} onChange={onTileSizeChange} />
                {tagFilter === null ? (
                  <Button
                    // quiet beside the solid Add pins, as list toolbars' controls are
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setTagFilter({ operator: 'matchAll', value: [] })
                    }
                  >
                    <Tag className="size-4" />
                    {t('filterByTag')}
                  </Button>
                ) : (
                  <EnumFilterWidget<string>
                    variant="plain"
                    filterName={t('tags')}
                    filterIcon={<Tag className="size-4" />}
                    value={tagFilter}
                    onValueChange={setTagFilter}
                    onRemove={() => setTagFilter(null)}
                    query={undefined}
                    config={{
                      valueType: 'array',
                      useValues: values =>
                        rd.of(values.map(value => ({ label: value, value }))),
                      useOptions: search =>
                        rd.of(
                          tagOptions
                            .filter(option =>
                              option
                                .toLowerCase()
                                .includes(search.toLowerCase())
                            )
                            .map(option => ({ label: option, value: option }))
                        )
                    }}
                  />
                )}
                {filtered && (
                  <span className="text-sm text-fg-muted">
                    {t('countFiltered', {
                      count: shown.length,
                      total: ordered.length
                    })}
                  </span>
                )}
              </div>
            )}
            {!readOnly && (
              <BatchActionsLayout
                selectedCount={selected.length}
                entityName={{ singular: t('pin'), plural: t('pins') }}
                onClearSelection={() => setPicked(new Set())}
                alternateContent={
                  <Button
                    variant="primaryAlt"
                    size="sm"
                    onClick={() => setAdding(true)}
                  >
                    <Plus className="size-4" />
                    {t('addPins')}
                  </Button>
                }
              >
                <DropdownMenuItem
                  className="text-fg-danger focus:text-fg-danger"
                  onSelect={() => remove(selected)}
                >
                  <Trash2 />
                  {t('deleteSelected', { count: selected.length })}
                </DropdownMenuItem>
              </BatchActionsLayout>
            )}
          </>
        }
        content={
          <div ref={boardRef} className="contents">
            {content}
          </div>
        }
      />
      <PinViewer
        pins={shown}
        open={viewerPinId !== null}
        onOpenChange={open => {
          if (!open) setViewerPinId(null);
        }}
        currentId={viewerPinId ?? undefined}
        onCurrentIdChange={setViewerPinId}
        // the sidebar is mounted and listening by now; the unit takes the focus
        onOpenAutoFocus={event => {
          if (!editTagsOnOpen.current) return;
          editTagsOnOpen.current = false;
          event.preventDefault();
          editTagsSignal.emit({ unitId: PIN_TAGS_UNIT_ID, fieldPath: null });
        }}
        sidebar={current => {
          const pin = shown.find(candidate => candidate.id === current?.id);
          return (
            pin && (
              <MediaItemSidebar
                selected={pin}
                readOnly={readOnly}
                allowCreateTags={allowCreateTags}
                editTagsSignal={editTagsSignal}
                handleUpdatePin={(_, updated) =>
                  void saveTags(pin, updated.tags ?? []).catch(() =>
                    toastService.open({
                      type: 'error',
                      message: t('toast.updateFailed')
                    })
                  )
                }
              />
            )
          );
        }}
      />
      {!readOnly && (
        <AddPinsDialog
          open={adding}
          onOpenChange={open => {
            setAdding(open);
            if (!open) setPastedLink(undefined);
          }}
          initialLink={pastedLink}
          onFiles={addFiles}
          onAddLink={write => void addLink(write)}
        />
      )}
    </>
  );
}
